import { useEffect, useState } from "react";
import { Database, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const fmtBytes = (b) => b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : b > 1e3 ? `${(b / 1e3).toFixed(0)} KB` : `${b} B`;

/** Which document store is live and how much of the archive it holds — owner only. */
export const StorageBadge = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/registry/storage").then(({ data }) => setS(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const migrate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/registry/storage/migrate");
      toast[data.moved ? "success" : "info"](data.moved ? `${data.moved} document(s) copied to S3` : "Previous store unreachable — will retry nightly");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return null;
  return (
    <div className="mt-6 border border-border bg-card p-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-testid="storage-badge">
      <span className="inline-flex items-center gap-2">
        <Database className="h-4 w-4 text-blue-900 dark:text-blue-400" />
        <span className="label-caps">Document store</span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${s.s3_live ? "bg-green-50 text-green-900" : "bg-amber-50 text-amber-900"}`} data-testid="storage-backend">
          {s.s3_live ? <ShieldCheck className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{s.backend}
        </span>
      </span>
      <span data-testid="storage-documents"><strong className="font-mono">{s.documents}</strong> documents · {fmtBytes(s.bytes)}</span>
      {s.legacy > 0 && (
        <span className="text-amber-800 dark:text-amber-400 inline-flex items-center gap-3" data-testid="storage-legacy">
          {s.legacy} still on the previous store
          <button onClick={migrate} disabled={busy} data-testid="storage-migrate-button"
            className="h-7 px-2 border border-amber-700/40 hover:bg-amber-100 dark:hover:bg-amber-950 uppercase tracking-widest text-[10px] transition-colors duration-200">
            {busy ? "Copying…" : "Copy to S3 now"}
          </button>
        </span>
      )}
      {s.legacy === 0 && s.s3_live && <span className="text-muted-foreground" data-testid="storage-all-s3">Entire archive in {s.bucket}</span>}
    </div>
  );
};
